import { DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR } from "@/constants";
import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { urlsBuilderContextToken } from "@/context_tokens";
import { growaveConfigurationToken } from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import { reviewsIconCollectionManagerContextToken } from "@apps/reviews/context_tokens";
import QuestionsQuery from "@apps/reviews/queries/QuestionsQuery";
import ReviewsFilterQuery from "@apps/reviews/queries/ReviewsFilterQuery";
import ReviewsQueryBase from "@apps/reviews/queries/ReviewsQueryBase";
import QuestionsService from "@apps/reviews/services/QuestionsService";
import ReviewsFilterService from "@apps/reviews/services/ReviewsFilterService";
import ReviewsServiceBase from "@apps/reviews/services/ReviewsServiceBase";
import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
import QuestionsStore from "@apps/reviews/stores/QuestionsStore";
import ReviewsFilterStore from "@apps/reviews/stores/ReviewsFilterStore";
import ReviewsStoreBase from "@apps/reviews/stores/ReviewsStoreBase";
import VotesStore from "@apps/reviews/stores/VotesStore";
import {
    reviewsAuthApiClientToken,
    reviewsIconCollectionManagerToken,
} from "@apps/reviews/tokens";
import ReviewsQuery from "@apps/reviews/widgets/reviews_product_page_widget/queries/ReviewsQuery";
import ReviewsService from "@apps/reviews/widgets/reviews_product_page_widget/services/ReviewsService";
import ReviewsStore from "@apps/reviews/widgets/reviews_product_page_widget/stores/ReviewsStore";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
import { CommonModule } from "@modules/common_module/CommonModule";
import { DesignModeProviderInterface } from "@modules/common_module/DesignModeProvider/DesignModeProviderInterface";
import GrowaveStorefrontValuesModule from "@modules/growave_storefront_values/GrowaveStorefrontValuesModule";
import GrowaveStorefrontValuesService from "@modules/growave_storefront_values/services/GrowaveStorefrontValuesService";
import { I18nModule } from "@modules/i18n/I18nModule";
import { container, injectable } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";
import { WidgetInterface } from "@interfaces/WidgetInterface";

import ReviewsGalleryManager from "../reviews_gallery_widget/ReviewsGalleryManager";
import ReviewsImagesSliderQuery from "./queries/ReviewsImagesSliderQuery";
import ReviewsProductPageWidgetComponent from "./ReviewsProductPageWidget.svelte";
import { ReviewsProductPageWidgetConfigInterface } from "./ReviewsProductPageWidgetConfig";
import ReviewsImagesSliderService from "./services/ReviewsImagesSliderService";
import ReviewsImagesSliderStore from "./stores/ReviewsImagesSliderStore";

@injectable()
class ReviewsProductPageWidget implements WidgetInterface {
    private readonly reviewsStore: ReviewsStore;
    private readonly reviewsService: ReviewsService;
    private readonly reviewsQuery: ReviewsQuery;
    private readonly questionsStore: QuestionsStore;
    private readonly questionsService: QuestionsService;
    private readonly questionsQuery: QuestionsQuery;
    private readonly reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    private readonly reviewsFilterQuery: ReviewsFilterQuery;
    private readonly reviewsFilterService: ReviewsFilterService;
    private readonly reviewsImagesSliderStore: ReviewsImagesSliderStore;
    private readonly reviewsImagesSliderService: ReviewsImagesSliderService;
    private readonly reviewsImagesSliderQuery: ReviewsImagesSliderQuery;
    private readonly reviewsGalleryManager: ReviewsGalleryManager;
    private readonly growaveStorefrontValuesService: GrowaveStorefrontValuesService;
    private readonly designModeProvider: DesignModeProviderInterface;

    constructor(
        private readonly config: ReviewsProductPageWidgetConfigInterface,
        private readonly logger: LoggerInterface,
        private readonly productIdManager: ProductIdManagerInterface
    ) {
        this.logger.debug("ReviewsProductPageWidget is initialized", config);
        const reviewsAuthApiClient = container.resolve(
            reviewsAuthApiClientToken
        );

        // Reviews
        this.reviewsStore = new ReviewsStoreBase();
        this.reviewsService = new ReviewsServiceBase(
            this.reviewsStore,
            container.resolve(VotesStore),
            reviewsAuthApiClient,
            this.logger
        );
        this.reviewsQuery = new ReviewsQueryBase(this.reviewsStore);

        // Reviews Images
        this.reviewsImagesSliderStore = new ReviewsImagesSliderStore();
        this.reviewsImagesSliderService = new ReviewsImagesSliderService(
            this.reviewsImagesSliderStore,
            reviewsAuthApiClient,
            this.logger
        );
        this.reviewsImagesSliderQuery = new ReviewsImagesSliderQuery(
            this.reviewsImagesSliderStore
        );

        // Questions
        this.questionsStore = new QuestionsStore();
        this.questionsService = new QuestionsService(
            this.questionsStore,
            reviewsAuthApiClient,
            this.logger
        );
        this.questionsQuery = new QuestionsQuery(this.questionsStore);

        this.reviewsStorefrontValuesService = container.resolve(
            ReviewsStorefrontValuesService
        );

        const reviewsFilterStore = new ReviewsFilterStore();
        this.reviewsFilterQuery = new ReviewsFilterQuery(reviewsFilterStore);
        this.reviewsFilterService = new ReviewsFilterService(
            reviewsFilterStore,
            container.resolve(reviewsAuthApiClientToken),
            this.logger
        );
        this.reviewsGalleryManager = container.resolve(ReviewsGalleryManager);

        this.growaveStorefrontValuesService = container.resolve(
            GrowaveStorefrontValuesModule
        ).exports.growaveStorefrontValuesService;

        this.designModeProvider =
            container.resolve(CommonModule).exports.designModeProvider;
    }

    async init(): Promise<this> {
        const growaveConfiguration = container.resolve(
            growaveConfigurationToken
        );

        const element = this.config.target;
        const shadowRoot = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        )
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        // I18n
        const i18nModule = container.resolve(I18nModule);
        i18nModule.exports.i18n.registerLoader(
            "reviews.ReviewsProductPageWidget",
            i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "reviews.ReviewsProductPageWidget"
            )
        );
        i18nModule.exports.i18n.registerLoader(
            "reviews.critical.ReviewsProductPageWidget",
            i18nModule.exports.i18nCriticalLoaderFactory.getLoader(
                "reviews.critical.ReviewsProductPageWidget"
            )
        );

        // inject Customizations
        const customizationManager = container.resolve(CustomizationManager);
        const customizationManagerConfig = {
            widgetName: REVIEWS_WIDGET_NAMES.REVIEWS_PRODUCT_PAGE_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromBlockCustomCss({
            element,
            shadowRoot,
            scriptSelector: DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR,
        });
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        const contextMap = new Map();
        contextMap.set(
            reviewsIconCollectionManagerContextToken,
            container.resolve(reviewsIconCollectionManagerToken)
        );
        contextMap.set(
            urlsBuilderContextToken,
            container.resolve(CommonModule).exports.urlsBuilder
        );

        new ReviewsProductPageWidgetComponent({
            target: shadowRoot || element,
            props: {
                productIdManager: this.productIdManager,

                minImagesForSliderVisibility:
                    this.config.minImagesForSliderVisibility,

                reviewsPerPage: this.config.reviewsPerPage,

                // Reviews
                reviewsQuery: this.reviewsQuery,
                reviewsService: this.reviewsService,

                // Questions
                questionsQuery: this.questionsQuery,
                questionsService: this.questionsService,

                reviewsStorefrontValuesService:
                    this.reviewsStorefrontValuesService,

                // Product review images
                reviewsImagesSliderQuery: this.reviewsImagesSliderQuery,
                reviewsImagesSliderService: this.reviewsImagesSliderService,

                // Review filters
                reviewsFilterQuery: this.reviewsFilterQuery,
                reviewsFilterService: this.reviewsFilterService,

                growaveStorefrontValuesService:
                    this.growaveStorefrontValuesService,
                designModeProvider: this.designModeProvider,
                reviewsGalleryManager: this.reviewsGalleryManager,

                afterWidgetInject: () => {
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    );
                },
            },
            context: contextMap,
        });

        return this;
    }
}

export default ReviewsProductPageWidget;
